import React, { useState, useEffect } from 'react';
import { subscriptionAPI } from '../services/api';
import paystackService from '../services/paystack.service';
import { toast } from 'react-toastify';

const UpgradePremiumModal = ({ isOpen, onClose, onSuccess }) => {
  const [plans,         setPlans]    = useState([]);
  const [selected,      setSelected] = useState(null);
  const [fetchingPlans, setFetching] = useState(true);
  const [paying,        setPaying]   = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFetching(true);
    subscriptionAPI.getPlans()
      .then(res => {
        const p = res.data.plans || [];
        setPlans(p);
        if (p.length) setSelected(p[0]);
      })
      .catch(() => {
        const fallback = [
          {
            id: 'premium_monthly',
            name: 'Premium Monthly',
            billing_cycle: 'monthly',
            price: 2000,
            currency: 'NGN',
            features: ['Unlimited tasks', 'Advanced burnout analytics', 'Priority buddy matching', 'Ad-free experience'],
          },
          {
            id: 'premium_yearly',
            name: 'Premium Yearly',
            billing_cycle: 'yearly',
            price: 20000,
            currency: 'NGN',
            features: ['Everything in Monthly', 'Save ~17%', 'Early access to new features'],
          },
        ];
        setPlans(fallback);
        setSelected(fallback[0]);
        toast.warn('Could not load plan details from server – using defaults.');
      })
      .finally(() => setFetching(false));
  }, [isOpen]);

  const handleUpgrade = async () => {
    if (!selected || paying) return;
    setPaying(true);

    await paystackService.processSubscription(
      {
        amount:      selected.price,
        billingCycle: selected.billing_cycle,
        planName:    selected.name,
      },
      // ── onSuccess: payment popup returned a reference ──────────────────
      async (response) => {
        try {
          const res = await subscriptionAPI.activate({
            payment_reference: response.reference,
            billing_cycle:     selected.billing_cycle,
          });
          toast.success('🎉 Premium activated successfully!');
          // Update cached user so the UI reflects premium immediately
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({ ...currentUser, user_type: 'premium' }));
          window.dispatchEvent(new Event('authChange'));
          onSuccess && onSuccess(res.data.subscription);
          onClose();
        } catch (err) {
          const msg = err.response?.data?.error
            || `Activation failed. Contact support with reference: ${response.reference}`;
          toast.error(msg, { autoClose: 10000 });
        } finally {
          setPaying(false);
        }
      },
      // ── onError: user closed popup or payment failed ───────────────────
      (err) => {
        setPaying(false);
        if (err?.message !== 'Payment window closed') {
          toast.error(err?.message || 'Payment failed. Please try again.');
        }
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={st.header}>
          <div style={st.crown}>👑</div>
          <h2 style={st.title}>Upgrade to Premium</h2>
          <p style={st.sub}>Unlock the full BestieConnect experience</p>
          <button style={st.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div style={st.body}>
          {fetchingPlans ? (
            <div style={st.loader}>Loading plans…</div>
          ) : (
            <>
              {/* Plan cards */}
              <div style={st.plansRow}>
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => !paying && setSelected(plan)}
                    onKeyDown={e => e.key === 'Enter' && !paying && setSelected(plan)}
                    style={{
                      ...st.planCard,
                      ...(selected?.id === plan.id ? st.planActive : {}),
                      cursor: paying ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {plan.billing_cycle === 'yearly' && (
                      <span style={st.badge}>Best Value</span>
                    )}
                    <div style={st.planName}>{plan.name}</div>
                    <div style={st.planPrice}>
                      ₦{(plan.price || 0).toLocaleString()}
                      <span style={st.planPer}>
                        /{plan.billing_cycle === 'yearly' ? 'yr' : 'mo'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature list */}
              {selected && (
                <ul style={st.features}>
                  {(selected.features || []).map((f, i) => (
                    <li key={i} style={st.feat}>
                      <span style={st.tick}>✔</span>{f}
                    </li>
                  ))}
                </ul>
              )}

              {/* Pay button */}
              <button
                style={{
                  ...st.cta,
                  opacity: (paying || !selected) ? 0.65 : 1,
                  cursor:  (paying || !selected) ? 'not-allowed' : 'pointer',
                }}
                onClick={handleUpgrade}
                disabled={paying || !selected}
              >
                {paying
                  ? '⏳ Processing…'
                  : `Pay ₦${(selected?.price || 0).toLocaleString()} & Upgrade`}
              </button>
              <p style={st.secure}>🔒 Secured by Paystack · Cancel anytime</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const st = {
  overlay:    { position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'16px' },
  modal:      { background:'#fff', borderRadius:'20px', width:'100%', maxWidth:'480px', boxShadow:'0 24px 60px rgba(108,99,255,0.25)', overflow:'hidden' },
  header:     { background:'linear-gradient(135deg,#6c63ff,#a78bfa)', padding:'32px 24px 24px', textAlign:'center', position:'relative' },
  crown:      { fontSize:'48px', marginBottom:'8px' },
  title:      { color:'#fff', margin:'0 0 6px', fontSize:'24px', fontWeight:700 },
  sub:        { color:'rgba(255,255,255,0.85)', margin:0, fontSize:'14px' },
  closeBtn:   { position:'absolute', top:'16px', right:'16px', background:'rgba(255,255,255,0.2)', border:'none', color:'#fff', borderRadius:'50%', width:'32px', height:'32px', cursor:'pointer', fontSize:'16px', lineHeight:'32px' },
  body:       { padding:'24px' },
  loader:     { textAlign:'center', color:'#888', padding:'32px 0' },
  plansRow:   { display:'flex', gap:'12px', marginBottom:'20px' },
  planCard:   { flex:1, border:'2px solid #e5e7eb', borderRadius:'12px', padding:'16px', textAlign:'center', position:'relative', userSelect:'none', transition:'all .15s' },
  planActive: { border:'2px solid #6c63ff', background:'#f5f3ff', boxShadow:'0 0 0 3px rgba(108,99,255,0.15)' },
  badge:      { position:'absolute', top:'-10px', left:'50%', transform:'translateX(-50%)', background:'#6c63ff', color:'#fff', fontSize:'10px', fontWeight:700, padding:'2px 10px', borderRadius:'20px', whiteSpace:'nowrap' },
  planName:   { fontSize:'13px', fontWeight:600, color:'#374151', marginBottom:'6px' },
  planPrice:  { fontSize:'22px', fontWeight:800, color:'#6c63ff' },
  planPer:    { fontSize:'13px', fontWeight:400, color:'#9ca3af' },
  features:   { listStyle:'none', padding:0, margin:'0 0 20px', display:'flex', flexDirection:'column', gap:'8px' },
  feat:       { display:'flex', alignItems:'center', gap:'8px', fontSize:'14px', color:'#374151' },
  tick:       { color:'#6c63ff', fontWeight:700, fontSize:'12px', flexShrink:0 },
  cta:        { width:'100%', padding:'14px', borderRadius:'12px', background:'linear-gradient(135deg,#6c63ff,#a78bfa)', color:'#fff', fontWeight:700, fontSize:'16px', border:'none', transition:'opacity .15s' },
  secure:     { textAlign:'center', color:'#9ca3af', fontSize:'12px', marginTop:'10px' },
};

export default UpgradePremiumModal;
